// Helper function to get full image URL from backend
// Use the value from vite config if available, otherwise fallback to import.meta.env
// @ts-ignore - This is defined in vite.config.ts
let API_BASE_URL = typeof __VITE_API_BASE_URL__ !== 'undefined'
  ? __VITE_API_BASE_URL__
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:4017');

// Convert HTTPS to HTTP for localhost in development (to avoid SSL errors)
// Keep HTTPS for production domains like rita366.com
if (import.meta.env.DEV && API_BASE_URL.startsWith('https://') && API_BASE_URL.includes('localhost')) {
  API_BASE_URL = API_BASE_URL.replace('https://', 'http://');
  console.warn('⚠️ [imageHelper] Converted HTTPS to HTTP for localhost development:', API_BASE_URL);
}

/**
 * Get full image URL from filename
 * @param filename - Image filename from API (e.g., "1764152695421-profilefarhan.png" or "/uploads/1764152695421-profilefarhan.png" or a full localhost URL)
 * @returns Full image URL pointing to the configured backend
 */
export const getImageUrl = (filename: string | null | undefined): string | null => {
  if (!filename || !filename.trim() || filename.trim().toLowerCase() === 'null') {
    return null;
  }

  let rawFilename = filename.trim();

  // Handle raw Google Places API photo references
  if (rawFilename.startsWith('places/')) {
    rawFilename = `/api/business/google-photo?name=${rawFilename}`;
  }

  // If it starts with a host prefix (http:// or https://), check if it needs proxying or return as-is
  if (rawFilename.startsWith('http://') || rawFilename.startsWith('https://')) {
    if (rawFilename.includes('emergencup.com')) {
      return rawFilename;
    }

    const isLocalhost = rawFilename.includes('localhost') || rawFilename.includes('127.0.0.1');
    const isPipelineIp = rawFilename.includes('82.112.236.108') || rawFilename.includes('167.86.118.137');
    
    if (isLocalhost || isPipelineIp) {
      try {
        const url = new URL(rawFilename);
        const cleanPath = url.pathname;
        if (cleanPath.includes('/uploads/')) {
          const filenamePart = cleanPath.split('/uploads/')[1];
          rawFilename = `/api/business/pipeline-photo/uploads/${filenamePart}`;
        } else {
          rawFilename = cleanPath + url.search;
        }
      } catch (e) {
        // Keep as-is if parsing fails
      }
    } else {
      // If it's a full external URL, return as-is
      if (import.meta.env.DEV && rawFilename.startsWith('https://') && rawFilename.includes('localhost')) {
        return rawFilename.replace('https://', 'http://');
      }
      return rawFilename;
    }
  }

  // Handle photo proxy URLs (both google-photo and pipeline-photo)
  if (rawFilename.startsWith('/api/business/') || rawFilename.includes('/google-photo') || rawFilename.includes('/pipeline-photo')) {
    try {
      return new URL(rawFilename, API_BASE_URL).toString();
    } catch (e) {
      const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const cleanPath = rawFilename.startsWith('/') ? rawFilename : `/${rawFilename}`;
      return `${base}${cleanPath}`;
    }
  }

  // Extract clean filename from uploads path
  let cleanFilename = rawFilename;
  if (cleanFilename.includes('/uploads/')) {
    const parts = cleanFilename.split('/uploads/');
    cleanFilename = parts[parts.length - 1];
  }

  // Remove leading slash
  if (cleanFilename.startsWith('/')) {
    cleanFilename = cleanFilename.substring(1);
  }
  
  // Remove uploads/ prefix if present
  if (cleanFilename.startsWith('uploads/')) {
    cleanFilename = cleanFilename.substring(8);
  }

  if (!cleanFilename || !cleanFilename.trim() || cleanFilename.trim() === '/') {
    return null;
  }
  
  try {
    return new URL(`/uploads/${cleanFilename}`, API_BASE_URL).toString();
  } catch (e) {
    const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    return `${base}/uploads/${cleanFilename}`;
  }
};

/**
 * Get profile image URL or return default avatar
 * @param filename - Image filename from API
 * @param defaultAvatar - Default avatar URL (optional)
 * @returns Profile image URL or default
 */
export const getProfileImageUrl = (
  filename: string | null | undefined,
  defaultAvatar?: string
): string => {
  const imageUrl = getImageUrl(filename);
  return imageUrl || defaultAvatar || '/default-avatar.png';
};
